import { Injectable, NgZone } from '@angular/core';
import { FetchResult } from '@apollo/client/core';
import { BrowserNotification, BrowserNotificationAction, PlusNotification, browserNotificationToPlusNotification, subscribeToNewNotifications } from '@karve.it/interfaces/notifications';

import { merge } from 'lodash';
import { Message, MessageService } from 'primeng/api';
import { Observable, Subject, Subscription } from 'rxjs';

import { PlusApollo } from '../auth/graphql.module';


/**
 * Returns the current unix time in seconds, with decimals
 */
 export function unixTime() {
  return Date.now() / 1000;
}

export interface BrowserLocalNotificationAction {
  label: string;
  handler?: (message: Message, action: BrowserLocalNotificationAction, event: Event) => void;
  class?: string;
  keepOpen?: boolean;
}

export interface CombinedNotification extends PlusNotification {
  webpush?: Notification;
}

export interface NotificationHandlerObject {
  [ key: string ]: (notification: CombinedNotification) => void;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {

  public receivedNotifications: CombinedNotification[] = [];
  public notificationHandlers: NotificationHandlerObject = {};

  public get notificationClicked() { return this.notificationClicked$.asObservable(); }
  public get notification() { return this.notification$.asObservable(); }

  private notificationClicked$ = new Subject<{
    notification: PlusNotification;
    action: BrowserNotificationAction
  }>();
  private notification$ = new Subject<{ notification: PlusNotification }>();
  private notificationObservable: Observable<FetchResult<{
      newNotification: PlusNotification;
  }, Record<string, any>, Record<string, any>>>;
  private notificationSubscription: Subscription;

  constructor(
    private ms: MessageService,
    private zone: NgZone,
    private apollo: PlusApollo,
  ) { }

  public async onBrowserNotificationClicked(
    notification: BrowserNotification,
    action: BrowserNotificationAction
  ) {
    this.onNotificationClicked(
      browserNotificationToPlusNotification(notification),
      action,
    );
  }

  public async onNotificationClicked(
    notification: PlusNotification,
    action: BrowserNotificationAction,
  ) {
    console.log('notification clicked', action, notification);
    // this._notificationClicked.next({
    //   action,
    //   notification,
    // });

    // resolve localNotification and update action
    // if (notification.data && notification.data.localNotificationId) {
    //   this.getNotification(notification.data.localNotificationId)
    //   .subscribe(async (n) => {
    //     if (!n.actions) { return; }
    //     const localAction = n.actions.find((a) => a.name === action);
    //     if (!localAction) { return; }
    //     console.log('notification action clicked', notification, n, localAction);
    //     await this.executeAction(n, localAction);
    //   });
    // }

    this.notificationClicked$.next({
      notification,
      action,
    });
  }

  public async onBrowserNotificationReceived(n: BrowserNotification) {
    this.onNewNotificationReceived(browserNotificationToPlusNotification(n));
  }

  public async onNewNotificationReceived(
    notification: PlusNotification & { webpush?: Notification },
  ) {

    const existingNotification = this.receivedNotifications.find((n) => n.id === notification.id);
    if (existingNotification) {
      merge(existingNotification, notification);
      return;
    }

    this.receivedNotifications.push(notification);
    console.log('onNewNotificationReceived', notification);
    this.notification$.next({
      notification,
    });

    if (notification.type && this.notificationHandlers[notification.type]) {
      this.notificationHandlers[notification.type](notification);
      return;
    }

    this.zone.run(() => {
      this.ms.add({
        id: notification.id,
        summary: notification.title,
        detail: notification.subtitle,
        sticky: notification.webpush && (notification.webpush as any).requireInteraction,
        life: notification.expiresIn + unixTime(),
        severity: (notification.webpush && notification.webpush.data.severity) || 'info',
        data: {
          ...notification,
          actions: notification.actions && notification.actions.map((a) => ({
            label: a.formattedName,
            handler: () => {
              this.onNotificationClicked(notification, {
                action: a.type,
                title: a.name,
              });
            },
          })) as BrowserLocalNotificationAction[],
        }
      });
    });
  }


  startWatchingNotifications() {
    if (this.notificationSubscription) {
      this.stopWatchingNotifications();
    }

    // console.log(`START WATCHING NOTIFICATIONS`);
    this.notificationObservable = this.apollo.subscribe<{ newNotification: PlusNotification }>({
      query: subscribeToNewNotifications,
    });

    this.notificationSubscription = this.notificationObservable.subscribe((res) => {
      // console.log('WATCHING', res);
      if (!res.data.newNotification) { return; }

      const n = res.data.newNotification;
      if (n.id && typeof n.data === 'string') {
        n.data = JSON.parse(n.data);
      }

      this.onNewNotificationReceived(n);
    }, (err) => {
      console.error(err);
      // this.stopWatchingNotifications();
    });
  }

  stopWatchingNotifications() {
    // console.log(`STOP WATCHING NOTIFICATIONS`);
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    delete this.notificationSubscription;
    delete this.notificationObservable;

  }
}
